<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOvvaBackend } from 'dashboard/composables/useOvvaBackend';

import SectionLayout from '../account/components/SectionLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();
const { callBackend } = useOvvaBackend();

const isReconnecting = ref(false);
const webhookResults = ref(null);
const webhookError = ref('');
const webhookByBrand = ref(null);

const CHANNEL_LABEL_KEYS = {
  telegram: 'AI_SETTINGS_MGMT.CHANNELS.TELEGRAM',
  whatsapp: 'AI_SETTINGS_MGMT.CHANNELS.WAZZUP',
  max: 'AI_SETTINGS_MGMT.CHANNELS.MAX',
};

// Бренды — только для подписи групп в разбивке ниже (name/code по sourceId).
// /admin/sources не требует PAT, поэтому грузим независимо от PAT-гейта.
const sourceNameById = ref({});
const loadSourceNames = async () => {
  try {
    const list = await callBackend('/admin/sources');
    if (Array.isArray(list)) {
      sourceNameById.value = Object.fromEntries(
        list.map(s => [s.id, s.name || s.code || `#${s.id}`])
      );
    }
  } catch (error) {
    // Не критично — разбивка по брендам просто покажет "Бренд #<id>".
  }
};

// Список ChannelRegistrationResult (per-brand/company) → сгруппированный по
// бренду/компании массив: [{ label, rows: [{channel, ok, message}] }].
const groupWebhookClients = rows => {
  if (!Array.isArray(rows) || !rows.length) return null;
  const groups = new Map();
  rows.forEach(row => {
    const key = `${row.clientId ?? ''}:${row.sourceId ?? ''}`;
    if (!groups.has(key)) {
      const label =
        row.sourceId != null
          ? sourceNameById.value[row.sourceId] || `Бренд #${row.sourceId}`
          : t('AI_SETTINGS_MGMT.CHANNELS.COMPANY_WIDE');
      groups.set(key, { key, label, rows: [] });
    }
    groups.get(key).rows.push(row);
  });
  return Array.from(groups.values());
};

// Ответ бэкенда с полем brands — каждый бренд и каждый канал, включая
// каналы, работающие ключом компании или общим ключом установки.
const ORIGIN_LABEL_KEYS = {
  brand: 'AI_SETTINGS_MGMT.CHANNELS.ORIGIN.BRAND',
  env: 'AI_SETTINGS_MGMT.CHANNELS.ORIGIN.ENV',
  company: 'AI_SETTINGS_MGMT.CHANNELS.ORIGIN.COMPANY',
  shared: 'AI_SETTINGS_MGMT.CHANNELS.ORIGIN.SHARED',
  none: 'AI_SETTINGS_MGMT.CHANNELS.ORIGIN.NONE',
};
const groupBrands = brands => {
  if (!Array.isArray(brands) || !brands.length) return null;
  return brands.map(brand => ({
    key: `brand-${brand.sourceId}`,
    label: brand.name || brand.code || `Бренд #${brand.sourceId}`,
    rows: (brand.channels || []).map(row => ({
      ...row,
      originLabel: ORIGIN_LABEL_KEYS[row.origin]
        ? t(ORIGIN_LABEL_KEYS[row.origin])
        : '',
    })),
  }));
};

const statusIcon = ok => {
  if (ok === true) return 'i-lucide-check-circle-2';
  if (ok === false) return 'i-lucide-x-circle';
  return 'i-lucide-circle-dashed';
};
const statusColor = ok => {
  if (ok === true) return 'text-n-teal-11';
  if (ok === false) return 'text-n-ruby-11';
  return 'text-n-slate-10';
};

// Блок переподключения каналов виден всегда (в т.ч. без PAT) — как в
// старой панели, ошибка показывается прямо в блоке, без общего PAT-гейта.
const reconnectWebhooks = async () => {
  isReconnecting.value = true;
  webhookError.value = '';
  webhookResults.value = null;
  webhookByBrand.value = null;
  try {
    if (!Object.keys(sourceNameById.value).length) await loadSourceNames();
    const r = await callBackend('/setup/register-webhooks', {
      method: 'POST',
    });
    webhookResults.value = [
      {
        key: 'telegram',
        label: t('AI_SETTINGS_MGMT.CHANNELS.TELEGRAM'),
        ...r?.telegram,
      },
      {
        key: 'wazzup',
        label: t('AI_SETTINGS_MGMT.CHANNELS.WAZZUP'),
        ...r?.wazzup,
      },
      { key: 'max', label: t('AI_SETTINGS_MGMT.CHANNELS.MAX'), ...r?.max },
    ];
    webhookByBrand.value =
      groupBrands(r?.brands) || groupWebhookClients(r?.clients);
  } catch (error) {
    webhookError.value = error.message || t('AI_SETTINGS_MGMT.CHANNELS.ERROR');
  } finally {
    isReconnecting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="$t('AI_SETTINGS_MGMT.CHANNELS.TITLE')"
    :description="$t('AI_SETTINGS_MGMT.CHANNELS.DESCRIPTION')"
  >
    <div class="flex items-center gap-3">
      <Button
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="$t('AI_SETTINGS_MGMT.CHANNELS.RECONNECT')"
        :is-loading="isReconnecting"
        @click="reconnectWebhooks"
      />
    </div>
    <p v-if="webhookError" class="mt-3 mb-0 text-body-main text-n-ruby-11">
      {{ webhookError }}
    </p>
    <ul
      v-if="webhookResults"
      class="flex flex-col gap-1.5 mt-3 mb-0 list-none"
    >
      <li
        v-for="item in webhookResults"
        :key="item.key"
        class="flex items-center gap-2 text-body-main"
      >
        <Icon
          :icon="item.ok ? 'i-lucide-check-circle-2' : 'i-lucide-x-circle'"
          class="flex-shrink-0 size-4"
          :class="item.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
        />
        <span class="text-n-slate-12">{{ item.label }}</span>
        <span class="text-n-slate-11">
          {{
            item.message ||
            (item.ok
              ? $t('AI_SETTINGS_MGMT.CHANNELS.OK')
              : $t('AI_SETTINGS_MGMT.CHANNELS.FAIL'))
          }}
        </span>
      </li>
    </ul>

    <div v-if="webhookByBrand" class="flex flex-col gap-3 mt-4">
      <p class="mb-0 text-body-main font-medium text-n-slate-12">
        {{ $t('AI_SETTINGS_MGMT.CHANNELS.BY_BRAND') }}
      </p>
      <div
        v-for="group in webhookByBrand"
        :key="group.key"
        class="flex flex-col gap-1.5 p-3 border rounded-lg border-n-weak"
      >
        <span class="text-body-two-bold text-n-slate-12">
          {{ group.label }}
        </span>
        <div
          v-for="(row, idx) in group.rows"
          :key="`${row.channel}-${idx}`"
          class="flex items-start gap-2 text-body-main"
        >
          <Icon
            :icon="statusIcon(row.ok)"
            class="flex-shrink-0 size-4 mt-0.5"
            :class="statusColor(row.ok)"
          />
          <span class="flex-shrink-0 text-n-slate-12">
            {{ $t(CHANNEL_LABEL_KEYS[row.channel] || row.channel) }}
          </span>
          <span
            v-if="row.originLabel"
            class="flex-shrink-0 px-1.5 rounded bg-n-alpha-2 text-label-small text-n-slate-11"
          >
            {{ row.originLabel }}
          </span>
          <span class="min-w-0 break-words text-n-slate-11">
            {{
              row.message ||
              (row.ok === null
                ? $t('AI_SETTINGS_MGMT.CHANNELS.NOT_TRACKED')
                : '')
            }}
          </span>
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
